import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MdArrowBack, MdClose, MdEdit, MdUpload } from "react-icons/md";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { productImageStyle } from "../productImage";
import { ProductIcon } from "./productsRedesign";
import "../new-order.css";
import "../products.css";

/* ------------------------------------------------------------------ *
 * Product editor (redesign) — /products/new and /products/:id/edit
 *
 * Replaces AddNewProductPage in src/pages/newProduct.js (kept at
 * /newProducts and /newProducts/:id):
 *   - three bootstrap columns of bare inputs -> four labelled cards in
 *                                               two columns
 *   - a save button in the page header       -> a sticky action bar that
 *                                               says what will happen
 *   - sizes and flower content edited through
 *     SummaryTable + ProductPriceContentDisplay
 *                                            -> add-a-row strips with
 *                                               inline edit and delete
 *   - a raw <input type=file> beside an
 *     "Upload Image" button                  -> one upload card that
 *                                               previews what is set
 *
 * The saved document is byte-for-byte what the legacy page sent, because
 * several screens read each field:
 *   sizes          JSON string { size: price }
 *   flower_content object      { size: [{flower_id, flower_name, amount}] }
 *   content        JSON string { size: { flowerName: amount }, arrayed: … }
 * `flower_content` is what /ordered-products reads; `content` is the
 * older map the detail panels still fall back to. Both are written.
 *
 * Endpoints unchanged: GET /products/getProduct/:id, POST
 * /products/addNew, POST /products/modifyProduct, GET
 * /api/flowers/get-all, GET /settings/getAll, POST
 * /upload-image/flowers (the products bucket the legacy page used).
 * ------------------------------------------------------------------ */

const SIZE_OPTIONS = [
  "One Size",
  "x-small",
  "small",
  "medium",
  "large",
  "x-large",
  "2x-large",
];

const safeParse = (raw, fallback) => {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const money = (v) => {
  const n = Number(v);
  return isFinite(n) ? `$${n % 1 === 0 ? n : n.toFixed(2)}` : `$${v}`;
};

/* Flowers by name, ignoring case ("ROSES - RED" next to "Roses - Pink")
   and reading numbers as numbers ("45CM" before "100CM"). */
export function byFlowerName(flowers) {
  return [...flowers].sort((a, b) =>
    String(a.flower_name || "").localeCompare(String(b.flower_name || ""), "en", {
      sensitivity: "base",
      numeric: true,
    })
  );
}

/* Older products only carry `content` (a per-size map of flower name to
   amount) and no ids. Read them into the same array shape the editor
   works in so they can be edited instead of silently emptied. */
function readContent(product) {
  const arrayed = product.flower_content || {};
  if (Object.keys(arrayed).length) {
    const out = {};
    Object.keys(arrayed).forEach((size) => {
      out[size] = (arrayed[size] || []).map((f) => ({
        flower_id: f.flower_id || "",
        flower_name: f.flower_name || "",
        amount: f.amount,
      }));
    });
    return out;
  }
  const legacy = safeParse(product.content, {}) || {};
  const out = {};
  Object.keys(legacy).forEach((size) => {
    if (size === "arrayed" || typeof legacy[size] !== "object") return;
    out[size] = Object.keys(legacy[size]).map((name) => ({
      flower_id: "",
      flower_name: name,
      amount: legacy[size][name],
    }));
  });
  return out;
}

export function ProductEditorRedesign({ editProduct }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [details, setDetails] = useState({
    product_name: "",
    product_type: "",
    description: "",
    code: "",
    image: "",
  });
  const [sizes, setSizes] = useState({});
  const [content, setContent] = useState({});

  const [types, setTypes] = useState([]);
  const [flowers, setFlowers] = useState([]);
  const [loading, setLoading] = useState(Boolean(editProduct));
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  /* The photo's display copies, for the preview only. Kept out of `details`
     so they are never sent on save (the server works them out itself). */
  const [imageVariants, setImageVariants] = useState(null);

  /* size add/edit strip */
  const [sizeDraft, setSizeDraft] = useState({ size: "", price: "", replacing: "" });
  /* flower add strip */
  const [activeSize, setActiveSize] = useState("");
  const [flowerDraft, setFlowerDraft] = useState({ flower_id: "", amount: "" });

  /* ---------------- load ---------------- */
  useEffect(() => {
    axios
      .get(BASE_SERVER_URL + "/settings/getAll")
      .then((doc) => {
        if (doc.data.status !== "success") return;
        setTypes(safeParse((doc.data.response[0] || {}).product_types, []) || []);
      })
      .catch(() => {});

    axios
      .get(BASE_SERVER_URL + "/api/flowers/get-all")
      .then((res) => {
        /* A–Z, so the flower wanted can be found in a long list */
        if (res.data.success) setFlowers(byFlowerName(res.data.data || []));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!editProduct) return;
    /* `loading` starts true in edit mode; without an id there is nothing
       to fetch, so say so instead of spinning forever. */
    if (!id) {
      setLoading(false);
      setLoadError(true);
      return;
    }
    setLoading(true);
    axios
      .get(BASE_SERVER_URL + "/products/getProduct/" + id)
      .then((res) => {
        const p = (res.data.response || [])[0];
        setLoading(false);
        if (!p) {
          setLoadError(true);
          return;
        }
        setDetails({
          product_name: p.product_name || "",
          product_type: p.product_type || "",
          description: p.description || "",
          code: p.code || "",
          image: p.image || "",
        });
        setImageVariants(p.image_variants || null);
        setSizes(safeParse(p.sizes, {}) || {});
        setContent(readContent(p));
      })
      .catch(() => {
        setLoading(false);
        setLoadError(true);
      });
  }, [editProduct, id]);

  const sizeKeys = useMemo(() => Object.keys(sizes), [sizes]);

  /* Keep the flower-content tab pointed at a size that still exists. */
  useEffect(() => {
    if (sizeKeys.length === 0) {
      if (activeSize) setActiveSize("");
    } else if (!sizeKeys.includes(activeSize)) {
      setActiveSize(sizeKeys[0]);
    }
  }, [sizeKeys, activeSize]);

  /* Sizes the dropdown offers: the standard list plus any custom size
     already on the product, minus the ones already priced. */
  const sizeChoices = useMemo(() => {
    const all = SIZE_OPTIONS.concat(
      sizeKeys.filter((s) => !SIZE_OPTIONS.includes(s))
    );
    return all.filter((s) => !sizeKeys.includes(s) || s === sizeDraft.replacing);
  }, [sizeKeys, sizeDraft.replacing]);

  const setField = (name, value) =>
    setDetails((prev) => ({ ...prev, [name]: value }));

  /* ---------------- sizes ---------------- */
  function commitSize() {
    const size = sizeDraft.size;
    const price = sizeDraft.price;
    if (!size || price === "") return;
    const previous = sizeDraft.replacing;

    setSizes((prev) => {
      const next = { ...prev };
      if (previous && previous !== size) delete next[previous];
      next[size] = price;
      return next;
    });
    /* Renaming a size carries its flower content across rather than
       orphaning it — the legacy page dropped it on the floor. */
    if (previous && previous !== size) {
      setContent((prev) => {
        const next = { ...prev };
        next[size] = next[previous] || [];
        delete next[previous];
        return next;
      });
    }
    setSizeDraft({ size: "", price: "", replacing: "" });
  }

  function editSize(size) {
    setSizeDraft({ size, price: sizes[size], replacing: size });
  }

  function removeSize(size) {
    setSizes((prev) => {
      const next = { ...prev };
      delete next[size];
      return next;
    });
    setContent((prev) => {
      const next = { ...prev };
      delete next[size];
      return next;
    });
    if (sizeDraft.replacing === size) {
      setSizeDraft({ size: "", price: "", replacing: "" });
    }
  }

  /* ---------------- flower content ---------------- */
  function addFlower() {
    const flower = flowers.find((f) => f._id === flowerDraft.flower_id);
    if (!flower || !flowerDraft.amount || !activeSize) return;
    setContent((prev) => {
      const list = (prev[activeSize] || []).filter(
        (f) => f.flower_id !== flower._id
      );
      return {
        ...prev,
        [activeSize]: list.concat({
          flower_id: flower._id,
          flower_name: flower.flower_name,
          amount: flowerDraft.amount,
        }),
      };
    });
    setFlowerDraft({ flower_id: "", amount: "" });
  }

  function removeFlower(size, index) {
    setContent((prev) => ({
      ...prev,
      [size]: (prev[size] || []).filter((_, i) => i !== index),
    }));
  }

  /* ---------------- image ---------------- */
  function uploadImage() {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("image", file);
    axios
      .post(BASE_SERVER_URL + "/upload-image/flowers", form)
      .then((res) => {
        setUploading(false);
        if (res.data.status === "success") {
          setField("image", res.data.fileName);
          setImageVariants(res.data.variants || null);
          setFile(null);
          toast.success("Image uploaded.");
        } else {
          toast.error("The server refused the upload.");
        }
      })
      .catch(() => {
        setUploading(false);
        toast.error("Couldn't upload the image.");
      });
  }

  /* ---------------- save ---------------- */
  const buildPayload = useCallback(() => {
    /* Write both shapes: `flower_content` is what /ordered-products
       reads; `content` is the older name->amount map, with `arrayed`
       alongside it exactly as the legacy page nested it. */
    const map = {};
    Object.keys(content).forEach((size) => {
      map[size] = {};
      (content[size] || []).forEach((f) => {
        map[size][f.flower_name] = f.amount;
      });
    });
    return {
      ...details,
      flower_content: content,
      content: JSON.stringify({ ...map, arrayed: content }),
      sizes: JSON.stringify(sizes),
    };
  }, [details, content, sizes]);

  const nameMissing = !details.product_name.trim();

  function save() {
    if (nameMissing) {
      toast.error("Give the product a display name first.");
      return;
    }
    setSaving(true);
    const payload = buildPayload();
    const url = editProduct
      ? BASE_SERVER_URL + "/products/modifyProduct"
      : BASE_SERVER_URL + "/products/addNew";
    const body = editProduct ? { id, updateData: payload } : payload;

    axios
      .post(url, body)
      .then((res) => {
        setSaving(false);
        if (res.data.status === "success") {
          toast.success(`Product ${editProduct ? "updated" : "added"}.`);
          navigate("/products");
        } else {
          toast.error(`Couldn't ${editProduct ? "update" : "add"} the product.`);
        }
      })
      .catch(() => {
        setSaving(false);
        toast.error("Couldn't reach the server.");
      });
  }

  if (loading) {
    return (
      <div className="pe-page">
        <div className="rf-empty">Loading product…</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="pe-page">
        <div className="rf-empty">
          Couldn&apos;t load that product.{" "}
          <button
            type="button"
            className="rf-btn rf-btn-ghost"
            onClick={() => navigate("/products")}
          >
            Back to products
          </button>
        </div>
      </div>
    );
  }

  const flowerList = content[activeSize] || [];
  const totalFlowers = Object.values(content).reduce(
    (n, list) => n + (list || []).length,
    0
  );

  return (
    <div className="pe-page">
      {/* ---------------- action bar ---------------- */}
      <div className="pe-bar">
        <button
          type="button"
          className="pe-back"
          onClick={() => navigate("/products")}
        >
          <MdArrowBack /> Products
        </button>
        <div className="pe-heading">
          <div className="pe-eyebrow">{editProduct ? "Editing" : "New"}</div>
          <h2 className="pe-title">
            {details.product_name || (editProduct ? "Product" : "New product")}
          </h2>
        </div>
        <span className="rf-spacer" />
        <span className="rf-form-bar-note">
          {sizeKeys.length} size{sizeKeys.length === 1 ? "" : "s"} · {totalFlowers}{" "}
          flower line{totalFlowers === 1 ? "" : "s"}
        </span>
        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          onClick={() => navigate("/products")}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rf-btn rf-btn-primary"
          onClick={save}
          disabled={saving || nameMissing}
          title={nameMissing ? "A display name is required" : undefined}
        >
          {saving ? "Saving…" : editProduct ? "Save changes" : "Add product"}
        </button>
      </div>

      <div className="pe-body">
        {/* ---------------- left: details + image ---------------- */}
        <div className="pe-col">
          <section className="pe-card">
            <div className="pe-card-head">
              <span className="pe-card-title">Details</span>
            </div>
            <div className="pe-card-body">
              <label className="rf-field">
                <span className="rf-label">Display name</span>
                <span
                  className={`rf-input-shell${nameMissing ? " is-error" : ""}`}
                >
                  <input
                    value={details.product_name}
                    placeholder="e.g. Classic Rose Wreath"
                    onChange={(e) => setField("product_name", e.target.value)}
                  />
                </span>
              </label>

              <div className="rf-grid-2">
                <label className="rf-field">
                  <span className="rf-label">Type</span>
                  <span className="rf-input-shell">
                    <input
                      list="pe-types"
                      value={details.product_type}
                      placeholder="Wreath"
                      onChange={(e) => setField("product_type", e.target.value)}
                    />
                  </span>
                  <datalist id="pe-types">
                    {types.map((t) => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>
                </label>

                <label className="rf-field">
                  <span className="rf-label">Code</span>
                  <span className="rf-input-shell">
                    <input
                      value={details.code}
                      placeholder="CC302"
                      onChange={(e) => setField("code", e.target.value)}
                    />
                  </span>
                </label>
              </div>

              <label className="rf-field">
                <span className="rf-label">Description</span>
                <span className="rf-input-shell is-area">
                  <textarea
                    rows={4}
                    value={details.description}
                    placeholder="What it is, when it suits, anything the florist should know."
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </span>
              </label>
            </div>
          </section>

          <section className="pe-card">
            <div className="pe-card-head">
              <span className="pe-card-title">Photo</span>
              {details.image && <span className="pe-card-note">Set</span>}
            </div>
            <div className="pe-card-body">
              <div className="pe-photo">
                <div
                  className="pe-preview"
                  style={productImageStyle(
                    { image: details.image, image_variants: imageVariants },
                    "medium"
                  )}
                >
                  {!details.image && <ProductIcon type={details.product_type} />}
                </div>

                <div className="pe-drop">
                  <span className="pe-drop-hint">
                    Choose an image, then upload it — the product keeps its
                    current photo until the upload finishes.
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files[0] || null)}
                  />
                  <button
                    type="button"
                    className="rf-btn rf-btn-ghost"
                    onClick={uploadImage}
                    disabled={!file || uploading}
                  >
                    <MdUpload style={{ verticalAlign: "-2px" }} />{" "}
                    {uploading ? "Uploading…" : "Upload image"}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* ---------------- right: sizes + flower content ---------------- */}
        <div className="pe-col">
          <section className="pe-card">
            <div className="pe-card-head">
              <span className="pe-card-title">Sizes &amp; prices</span>
              <span className="pe-card-note rf-num">{sizeKeys.length}</span>
            </div>
            <div className="pe-card-body">
              <div className="pe-add">
                <label className="rf-field">
                  <span className="rf-label">Size</span>
                  <span className="rf-input-shell">
                    <select
                      value={sizeDraft.size}
                      onChange={(e) =>
                        setSizeDraft((d) => ({ ...d, size: e.target.value }))
                      }
                    >
                      <option value="">Choose a size</option>
                      {sizeChoices.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
                <label className="rf-field">
                  <span className="rf-label">Price</span>
                  <span className="rf-input-shell">
                    <input
                      type="number"
                      min="0"
                      value={sizeDraft.price}
                      placeholder="0.00"
                      onChange={(e) =>
                        setSizeDraft((d) => ({ ...d, price: e.target.value }))
                      }
                    />
                  </span>
                </label>
                <button
                  type="button"
                  className="rf-btn rf-btn-ghost"
                  onClick={commitSize}
                  disabled={!sizeDraft.size || sizeDraft.price === ""}
                >
                  {sizeDraft.replacing ? "Update" : "Add"}
                </button>
              </div>

              <div className="pe-rows">
                {sizeKeys.length === 0 && (
                  <div className="pe-empty">
                    No sizes yet — a product needs at least one to be orderable.
                  </div>
                )}
                {sizeKeys.map((s) => (
                  <div
                    className={`pe-row${sizeDraft.replacing === s ? " is-editing" : ""}`}
                    key={s}
                  >
                    <span className="pe-row-k">{s}</span>
                    <span className="pe-row-v">{money(sizes[s])}</span>
                    <span className="pe-row-acts">
                      <button
                        type="button"
                        className="pe-mini"
                        title="Edit this size"
                        onClick={() => editSize(s)}
                      >
                        <MdEdit />
                      </button>
                      <button
                        type="button"
                        className="pe-mini is-danger"
                        title="Remove this size"
                        onClick={() => removeSize(s)}
                      >
                        <MdClose />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="pe-card">
            <div className="pe-card-head">
              <span className="pe-card-title">Flower content</span>
              <span className="pe-card-note rf-num">{totalFlowers}</span>
            </div>
            <div className="pe-card-body">
              {sizeKeys.length === 0 ? (
                <div className="pe-empty">
                  Add a size first — flower content is recorded per size.
                </div>
              ) : (
                <>
                  <div className="pe-tabs">
                    {sizeKeys.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`pe-tab${s === activeSize ? " is-active" : ""}`}
                        onClick={() => setActiveSize(s)}
                      >
                        {s}
                        <span className="pe-tab-n">
                          {(content[s] || []).length}
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="pe-add">
                    <label className="rf-field">
                      <span className="rf-label">Flower</span>
                      <span className="rf-input-shell">
                        <select
                          value={flowerDraft.flower_id}
                          onChange={(e) =>
                            setFlowerDraft((d) => ({
                              ...d,
                              flower_id: e.target.value,
                            }))
                          }
                        >
                          <option value="">Choose a flower</option>
                          {flowers.map((f) => (
                            <option key={f._id} value={f._id}>
                              {f.flower_name}
                            </option>
                          ))}
                        </select>
                      </span>
                    </label>
                    <label className="rf-field">
                      <span className="rf-label">Stems</span>
                      <span className="rf-input-shell">
                        <input
                          type="number"
                          min="1"
                          value={flowerDraft.amount}
                          placeholder="0"
                          onChange={(e) =>
                            setFlowerDraft((d) => ({ ...d, amount: e.target.value }))
                          }
                        />
                      </span>
                    </label>
                    <button
                      type="button"
                      className="rf-btn rf-btn-ghost"
                      onClick={addFlower}
                      disabled={!flowerDraft.flower_id || !flowerDraft.amount}
                    >
                      Add
                    </button>
                  </div>

                  <div className="pe-rows">
                    {flowerList.length === 0 && (
                      <div className="pe-empty">
                        Nothing recorded for {activeSize} yet.
                      </div>
                    )}
                    {flowerList.map((f, i) => (
                      <div className="pe-row" key={f.flower_id || `${f.flower_name}-${i}`}>
                        <span className="pe-row-k">
                          {f.flower_name || "Unnamed flower"}
                        </span>
                        <span className="pe-row-v">×{f.amount}</span>
                        <span className="pe-row-acts">
                          <button
                            type="button"
                            className="pe-mini is-danger"
                            title="Remove this flower"
                            onClick={() => removeFlower(activeSize, i)}
                          >
                            <MdClose />
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default ProductEditorRedesign;
